import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import ExcelJS from 'exceljs'
import {
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  Tab,
  Nav,
  Card,
  Table,
  Spinner,
  Alert,
  Badge,
  Modal,
} from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import apiServices from '../services/apiServices'

const TAB_META = [
  { key: 'facebook_usd', label: 'Facebook 美金', currency: 'USD' },
  { key: 'facebook_twd', label: 'Facebook 台幣', currency: 'TWD' },
  { key: 'criteo', label: 'Criteo', currency: 'USD' },
  { key: 'twitter', label: 'Twitter', currency: 'USD' },
  { key: 'linkedin', label: 'LinkedIn', currency: 'USD' },
  { key: 'google', label: 'Google', currency: 'USD' },
]

const TAB_TO_MEDIA_KEY = {
  facebook_usd: 'facebook_美金',
  facebook_twd: 'facebook_台幣',
  criteo: 'criteo',
  twitter: 'twitter',
  linkedin: 'linkedin',
  google: 'google',
}

const TAB_DEFAULT_MEDIA = {
  facebook_usd: 'Meta',
  facebook_twd: 'Meta',
  criteo: 'Criteo',
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
  google: 'Google',
}

const REVENUE_VIEW_MODES = {
  system: 'system',
  user: 'user',
}

const REVENUE_VIEW_TABS = [
  { key: REVENUE_VIEW_MODES.system, label: '系統比對' },
  { key: REVENUE_VIEW_MODES.user, label: '依使用者設定' },
]

const createRevenueState = (defaultValue) => ({
  [REVENUE_VIEW_MODES.system]: defaultValue,
  [REVENUE_VIEW_MODES.user]: defaultValue,
})

const REVENUE_HEADER_CELL_STYLE = {
  position: 'sticky',
  top: 0,
  background: '#fff',
  zIndex: 3,
  boxShadow: 'inset 0 -1px 0 rgba(0, 0, 0, 0.075)',
}

const DEFAULT_MONTH = '2025-01'

const PROFIT_VALIDATION_STATUS_META = {
  match: {
    label: '吻合',
    variant: 'success',
  },
  mismatch: {
    label: '金額不一致',
    variant: 'danger',
  },
  manual_only: {
    label: '僅手動資料',
    variant: 'warning',
    text: 'dark',
  },
  generated_only: {
    label: '僅貼表資料',
    variant: 'info',
    text: 'dark',
  },
}

const PROFIT_VALIDATION_STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'match', label: '吻合' },
  { value: 'mismatch', label: '金額不一致' },
  { value: 'manual_only', label: '僅手動資料' },
  { value: 'generated_only', label: '僅貼表資料' },
]

const PROFIT_VALIDATION_STATUS_BADGE_OPTIONS =
  PROFIT_VALIDATION_STATUS_OPTIONS.filter((option) => option.value)

const formatNumber = (value) => {
  const amount = Number(value) || 0
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const getMonthRange = (value) => {
  const parsed = moment(value, 'YYYY-MM', true)
  if (!parsed.isValid()) {
    return {
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD'),
    }
  }
  return {
    startDate: parsed.clone().startOf('month').format('YYYY-MM-DD'),
    endDate: parsed.clone().endOf('month').format('YYYY-MM-DD'),
  }
}

const formatMonthParam = (value) => (value || DEFAULT_MONTH).replace('-', '')

const formatTimestamp = (value) =>
  value ? moment(value).format('YYYY/MM/DD HH:mm') : '-'

const formatSignedNumber = (value) => {
  const numeric = Number(value) || 0
  const absoluteFormatted = formatNumber(Math.abs(numeric))
  if (numeric > 0) {
    return `+${absoluteFormatted}`
  }
  if (numeric < 0) {
    return `-${absoluteFormatted}`
  }
  return formatNumber(0)
}

function SummaryCard({ title, value, subTitle }) {
  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        <Card.Title className="text-muted small mb-1">{title}</Card.Title>
        <Card.Text className="fs-4 fw-bold">{value}</Card.Text>
        {subTitle && <div className="text-muted small">{subTitle}</div>}
      </Card.Body>
    </Card>
  )
}

export default function MediaCampaignOverview() {
  const initialRange = useMemo(() => getMonthRange(DEFAULT_MONTH), [])
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH)
  const [inputRange, setInputRange] = useState(initialRange)
  const [appliedRange, setAppliedRange] = useState(initialRange)
  const [tabsData, setTabsData] = useState([])
  const [budgetData, setBudgetData] = useState({
    rows: [],
    mediaTotals: [],
    totalAmount: 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [activeKey, setActiveKey] = useState(TAB_META[0].key)
  const [showBudgetModal, setShowBudgetModal] = useState(false)
  const [showAllocationModal, setShowAllocationModal] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [allocationRows, setAllocationRows] = useState([])
  const [allocationMap, setAllocationMap] = useState({})
  const [savedAllocations, setSavedAllocations] = useState({})
  const [allocationHistoryMap, setAllocationHistoryMap] = useState({})
  const [allocationHistoryLoading, setAllocationHistoryLoading] =
    useState(false)
  const [historyLoadingCampaignId, setHistoryLoadingCampaignId] = useState(null)
  const [allocationSaveError, setAllocationSaveError] = useState('')
  const [savingAllocation, setSavingAllocation] = useState(false)
  const [exportingRevenue, setExportingRevenue] = useState(false)
  const [exportError, setExportError] = useState('')
  const [showRevenueModal, setShowRevenueModal] = useState(false)
  const [revenueViewMode, setRevenueViewMode] = useState(
    REVENUE_VIEW_MODES.system
  )
  const [revenueDataByMode, setRevenueDataByMode] = useState(() =>
    createRevenueState(null)
  )
  const [revenueLoadingByMode, setRevenueLoadingByMode] = useState(() =>
    createRevenueState(false)
  )
  const [revenueErrorByMode, setRevenueErrorByMode] = useState(() =>
    createRevenueState('')
  )
  const [revenueTabKey, setRevenueTabKey] = useState('')
  const [revenueMonth, setRevenueMonth] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showProfitModal, setShowProfitModal] = useState(false)
  const [profitData, setProfitData] = useState({
    rows: [],
    mediaTotals: [],
    totalAmount: 0,
    availableMedia: [],
  })
  const [profitMonth, setProfitMonth] = useState('')
  const [profitLoading, setProfitLoading] = useState(false)
  const [profitError, setProfitError] = useState('')
  const [profitSearchTerm, setProfitSearchTerm] = useState('')
  const [profitMediaFilter, setProfitMediaFilter] = useState('')
  const [showProfitValidationModal, setShowProfitValidationModal] =
    useState(false)
  const [profitValidationLoading, setProfitValidationLoading] = useState(false)
  const [profitValidationError, setProfitValidationError] = useState('')
  const [profitValidationData, setProfitValidationData] = useState({
    month: '',
    summary: null,
    differences: [],
  })
  const [profitValidationSearchTerm, setProfitValidationSearchTerm] =
    useState('')
  const [profitValidationStatusFilter, setProfitValidationStatusFilter] =
    useState('')

  const currentRevenueData = revenueDataByMode[revenueViewMode] || null
  const currentRevenueLoading = revenueLoadingByMode[revenueViewMode] || false
  const currentRevenueError = revenueErrorByMode[revenueViewMode] || ''
  const profitMonthLabel = useMemo(() => {
    if (profitMonth && profitMonth.length === 6) {
      return `${profitMonth.slice(0, 4)}-${profitMonth.slice(4)}`
    }
    return selectedMonth
  }, [profitMonth, selectedMonth])
  const profitAvailableMedia = useMemo(() => {
    const medias = Array.isArray(profitData.availableMedia)
      ? profitData.availableMedia
      : []
    return [...new Set(medias.filter(Boolean))].sort()
  }, [profitData.availableMedia])
  const profitMediaOptions = useMemo(() => {
    if (
      profitMediaFilter &&
      !profitAvailableMedia.includes(profitMediaFilter)
    ) {
      return [profitMediaFilter, ...profitAvailableMedia]
    }
    return profitAvailableMedia
  }, [profitAvailableMedia, profitMediaFilter])
  const profitValidationSummary = profitValidationData.summary
  const profitValidationRows = profitValidationData.differences || []
  const profitValidationCounts = useMemo(
    () => ({
      match: profitValidationSummary?.counts?.match || 0,
      mismatch: profitValidationSummary?.counts?.mismatch || 0,
      manual_only: profitValidationSummary?.counts?.manual_only || 0,
      generated_only: profitValidationSummary?.counts?.generated_only || 0,
    }),
    [profitValidationSummary]
  )
  const profitValidationMonthLabel = useMemo(() => {
    if (profitValidationData.month && profitValidationData.month.length === 6) {
      const validationYear = profitValidationData.month.slice(0, 4)
      const validationMonth = profitValidationData.month.slice(4)
      return `${validationYear}-${validationMonth}`
    }
    return selectedMonth
  }, [profitValidationData.month, selectedMonth])
  const filteredProfitRows = useMemo(() => {
    const term = profitSearchTerm.trim().toLowerCase()
    const mediaFilter = profitMediaFilter.trim().toLowerCase()
    return (profitData.rows || []).filter((row) => {
      const matchesTerm = term
        ? [row.projectName, row.projectCode, row.clientName, row.media].some(
            (value) =>
              String(value ?? '')
                .toLowerCase()
                .includes(term)
          )
        : true
      const matchesMedia = mediaFilter
        ? String(row.media || '').toLowerCase() === mediaFilter
        : true
      return matchesTerm && matchesMedia
    })
  }, [profitData.rows, profitMediaFilter, profitSearchTerm])
  const filteredProfitTotal = useMemo(
    () =>
      filteredProfitRows.reduce(
        (sum, row) => sum + (Number(row.amount) || 0),
        0
      ),
    [filteredProfitRows]
  )
  const filteredProfitMediaTotals = useMemo(
    () =>
      Object.entries(
        filteredProfitRows.reduce((acc, row) => {
          const mediaName = row.media || '未分類'
          acc[mediaName] = (acc[mediaName] || 0) + (Number(row.amount) || 0)
          return acc
        }, {})
      ).map(([media, amount]) => ({ media, amount })),
    [filteredProfitRows]
  )
  const filteredProfitTargetedCount = useMemo(
    () => filteredProfitRows.filter((row) => row.isTargeted).length,
    [filteredProfitRows]
  )
  const filteredProfitValidationRows = useMemo(() => {
    const term = profitValidationSearchTerm.trim().toLowerCase()
    const statusFilter = profitValidationStatusFilter
    return profitValidationRows.filter((row) => {
      const matchesTerm = term
        ? [
            row.projectName,
            row.projectCode,
            row.clientName,
            row.manualMedia,
            row.generatedMedia,
            row.generatedRemark,
          ].some((value) =>
            String(value ?? '')
              .toLowerCase()
              .includes(term)
          )
        : true
      const matchesStatus = statusFilter ? row.status === statusFilter : true
      return matchesTerm && matchesStatus
    })
  }, [
    profitValidationRows,
    profitValidationSearchTerm,
    profitValidationStatusFilter,
  ])

  const tabsMap = useMemo(
    () =>
      tabsData.reduce((acc, tab) => {
        acc[tab.key] = tab
        return acc
      }, {}),
    [tabsData]
  )

  const activeTabMeta = useMemo(
    () => TAB_META.find((tab) => tab.key === activeKey) || TAB_META[0],
    [activeKey]
  )

  const revenueTabMeta = useMemo(() => {
    if (!revenueTabKey) return activeTabMeta
    return TAB_META.find((tab) => tab.key === revenueTabKey) || activeTabMeta
  }, [revenueTabKey, activeTabMeta])

  const revenueMonthLabel = useMemo(() => {
    if (revenueMonth && revenueMonth.length === 6) {
      return `${revenueMonth.slice(0, 4)}-${revenueMonth.slice(4)}`
    }
    return selectedMonth
  }, [revenueMonth, selectedMonth])

  const currentMonthParam = useMemo(
    () => formatMonthParam(selectedMonth),
    [selectedMonth]
  )

  const filteredBudgetRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return (budgetData.rows || []).filter((row) => {
      if (!term) return true
      return [row.projectName, row.projectCode]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    })
  }, [budgetData.rows, searchTerm])

  const projectOptions = useMemo(() => {
    const map = new Map()
    ;(budgetData.rows || []).forEach((row) => {
      if (!row.projectCode) return
      if (!map.has(row.projectCode)) {
        map.set(row.projectCode, {
          projectCode: row.projectCode,
          projectName: row.projectName || '',
          label: `${row.projectCode} - ${row.projectName || '未命名專案'}`,
        })
      }
    })
    return Array.from(map.values())
  }, [budgetData.rows])

  const selectedCampaignMeta = useMemo(() => {
    if (!selectedCampaign) return null
    return savedAllocations[selectedCampaign.campaignId] || null
  }, [savedAllocations, selectedCampaign])

  const selectedCampaignHistory = useMemo(() => {
    if (!selectedCampaign) return []
    return allocationHistoryMap[selectedCampaign.campaignId] || []
  }, [allocationHistoryMap, selectedCampaign])

  const hasHistoryLoaded = useMemo(() => {
    if (!selectedCampaign) return false
    return Object.prototype.hasOwnProperty.call(
      allocationHistoryMap,
      selectedCampaign.campaignId
    )
  }, [allocationHistoryMap, selectedCampaign])

  const isHistoryLoading = Boolean(
    selectedCampaign &&
      allocationHistoryLoading &&
      historyLoadingCampaignId === selectedCampaign.campaignId
  )

  const filterCampaigns = useCallback(
    (campaigns) => {
      const term = searchTerm.trim().toLowerCase()
      return campaigns.filter((campaign) => {
        if (!term) return true
        return [
          campaign.campaignName,
          campaign.campaignId,
          campaign.accountName,
          campaign.accountId,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term))
      })
    },
    [searchTerm]
  )

  const createEmptyAllocationRow = useCallback(
    () => ({
      id: `${Date.now()}-${Math.random()}`,
      projectCode: '',
      projectName: '',
      amount: '',
      ac: '',
    }),
    []
  )

  const handleCampaignClick = useCallback(
    (campaign, tabKey) => {
      if (!campaign) return
      const mediaKey = TAB_TO_MEDIA_KEY[tabKey] || campaign.mediaKey || ''
      setSelectedCampaign({ ...campaign, tabKey, mediaKey })
      const existingAllocations = allocationMap[campaign.campaignId] || []
      if (existingAllocations.length) {
        setAllocationRows(
          existingAllocations.map((item) => ({
            id: `${Date.now()}-${Math.random()}`,
            projectCode: item.projectCode,
            projectName: item.projectName,
            amount: item.amount,
            ac: item.ac,
          }))
        )
      } else {
        setAllocationRows([createEmptyAllocationRow()])
      }
      setShowAllocationModal(true)
    },
    [allocationMap, createEmptyAllocationRow]
  )

  const handleAllocationModalClose = useCallback(() => {
    setShowAllocationModal(false)
    setSelectedCampaign(null)
    setAllocationRows([])
    setAllocationSaveError('')
    setHistoryLoadingCampaignId(null)
  }, [])

  const loadSavedAllocations = useCallback(async (monthParam) => {
    if (!monthParam) return
    try {
      const response = await apiServices.data({
        path: 'billing/campaign-allocations',
        method: 'get',
        params: { month: monthParam },
      })
      if (response?.error) {
        throw response.error
      }
      const latestMap = {}
      const metadata = {}
      ;(response?.records || []).forEach((record) => {
        if (!record?.campaignId) return
        latestMap[record.campaignId] = record.allocations || []
        metadata[record.campaignId] = record
      })
      setAllocationMap(latestMap)
      setSavedAllocations(metadata)
      setAllocationHistoryMap({})
      setAllocationSaveError('')
    } catch (err) {
      setAllocationSaveError('無法載入既有分配，請稍後再試。')
    }
  }, [])

  const loadAllocationHistory = useCallback(async (campaignId, monthParam) => {
    if (!campaignId) return
    setAllocationHistoryLoading(true)
    setHistoryLoadingCampaignId(campaignId)
    try {
      const response = await apiServices.data({
        path: `billing/campaign-allocations/${campaignId}/history`,
        method: 'get',
        params: monthParam ? { month: monthParam } : {},
      })
      if (response?.error) {
        throw response.error
      }
      setAllocationHistoryMap((prev) => ({
        ...prev,
        [campaignId]: response.records || [],
      }))
      setAllocationSaveError('')
    } catch (err) {
      setAllocationSaveError('無法取得歷史紀錄，請稍後再試。')
    } finally {
      setAllocationHistoryLoading(false)
      setHistoryLoadingCampaignId(null)
    }
  }, [])

  const handleViewAllocationHistory = useCallback(() => {
    if (!selectedCampaign?.campaignId) return
    loadAllocationHistory(selectedCampaign.campaignId, currentMonthParam)
  }, [currentMonthParam, loadAllocationHistory, selectedCampaign])

  const handleRevenueViewModeChange = useCallback(
    (mode) => {
      if (!mode || mode === revenueViewMode) return
      setRevenueViewMode(mode)
    },
    [revenueViewMode]
  )

  const handleAllocationProjectChange = useCallback(
    (rowId, projectCode) => {
      const option = projectOptions.find(
        (opt) => opt.projectCode === projectCode
      )
      setAllocationRows((rows) =>
        rows.map((row) =>
          row.id === rowId
            ? {
                ...row,
                projectCode: option?.projectCode || '',
                projectName: option?.projectName || '',
              }
            : row
        )
      )
    },
    [projectOptions]
  )

  const handleAllocationFieldChange = useCallback((rowId, field, value) => {
    setAllocationRows((rows) =>
      rows.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    )
  }, [])

  const handleAddAllocationRow = useCallback(() => {
    setAllocationRows((rows) => [...rows, createEmptyAllocationRow()])
  }, [createEmptyAllocationRow])

  const handleRemoveAllocationRow = useCallback(
    (rowId) => {
      setAllocationRows((rows) => {
        const updated = rows.filter((row) => row.id !== rowId)
        return updated.length ? updated : [createEmptyAllocationRow()]
      })
    },
    [createEmptyAllocationRow]
  )

  const allocationTotal = useMemo(
    () =>
      allocationRows.reduce(
        (sum, row) => sum + (Number(row.amount) > 0 ? Number(row.amount) : 0),
        0
      ),
    [allocationRows]
  )

  const campaignSpend = useMemo(
    () => (selectedCampaign ? Number(selectedCampaign.spend) || 0 : 0),
    [selectedCampaign]
  )

  const allocationRowsValid = useMemo(
    () =>
      allocationRows.length > 0 &&
      allocationRows.every(
        (row) => row.projectCode && Number(row.amount) > 0 && row.amount !== ''
      ),
    [allocationRows]
  )

  const allocationMatchesTotal = useMemo(
    () => selectedCampaign && Math.abs(allocationTotal - campaignSpend) < 0.01,
    [allocationTotal, campaignSpend, selectedCampaign]
  )

  const isAllocationSaveEnabled = Boolean(
    selectedCampaign && allocationRowsValid && allocationMatchesTotal
  )

  const handleAllocationSave = useCallback(async () => {
    if (!selectedCampaign?.campaignId) return
    if (!currentMonthParam) {
      setAllocationSaveError('請先選擇月份後再儲存分配。')
      return
    }

    const payloadAllocations = allocationRows
      .filter((row) => row.projectCode)
      .map((row) => ({
        projectCode: row.projectCode,
        projectName: row.projectName,
        amount: Number(row.amount) || 0,
        ac:
          row.ac !== undefined && row.ac !== null && row.ac !== ''
            ? Number(row.ac)
            : null,
      }))

    const payload = {
      month: currentMonthParam,
      campaignId: selectedCampaign.campaignId,
      campaignName: selectedCampaign.campaignName,
      accountId: selectedCampaign.accountId,
      accountName: selectedCampaign.accountName,
      provider: selectedCampaign.provider || selectedCampaign.mediaKey || '',
      mediaKey:
        selectedCampaign.mediaKey ||
        TAB_TO_MEDIA_KEY[selectedCampaign.tabKey] ||
        '',
      currency: selectedCampaign.currency,
      allocations: payloadAllocations,
    }

    setSavingAllocation(true)
    setAllocationSaveError('')
    try {
      const response = await apiServices.data({
        path: 'billing/campaign-allocations',
        method: 'post',
        data: payload,
      })
      if (response?.error) {
        throw response.error
      }
      const record = response?.record || {
        ...payload,
        allocations: payloadAllocations,
        createdAt: new Date().toISOString(),
      }
      setAllocationMap((prev) => ({
        ...prev,
        [selectedCampaign.campaignId]: record.allocations || payloadAllocations,
      }))
      setSavedAllocations((prev) => ({
        ...prev,
        [selectedCampaign.campaignId]: record,
      }))
      setAllocationHistoryMap((prev) => {
        const existingHistory = prev[selectedCampaign.campaignId] || []
        return {
          ...prev,
          [selectedCampaign.campaignId]: record
            ? [record, ...existingHistory]
            : existingHistory,
        }
      })
      handleAllocationModalClose()
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        '儲存分配紀錄時發生錯誤，請稍後再試。'
      setAllocationSaveError(message)
    } finally {
      setSavingAllocation(false)
    }
  }, [
    allocationRows,
    currentMonthParam,
    handleAllocationModalClose,
    selectedCampaign,
  ])

  const fetchRevenueSheetData = useCallback(
    async (monthParam, tabKey, viewMode = REVENUE_VIEW_MODES.system) => {
      const response = await apiServices.data({
        path: 'billing/revenue-sheet',
        method: 'get',
        params: { month: monthParam, tabKey, viewMode },
      })
      if (response?.error) {
        throw response.error
      }
      return response
    },
    []
  )

  const loadProfitTable = useCallback(async (monthParam) => {
    if (!monthParam) return
    setProfitLoading(true)
    setProfitError('')
    try {
      const response = await apiServices.data({
        path: 'billing/profit-table',
        method: 'get',
        params: { month: monthParam },
      })
      if (response?.error) {
        throw response.error
      }
      setProfitMonth(response?.month || monthParam)
      setProfitData({
        rows: response?.rows || [],
        mediaTotals: response?.mediaTotals || [],
        totalAmount: response?.totalAmount || 0,
        availableMedia: response?.availableMedia || [],
      })
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        '取得利潤表資料時發生錯誤，請稍後再試。'
      setProfitError(message)
    } finally {
      setProfitLoading(false)
    }
  }, [])

  const handleOpenProfitModal = useCallback(() => {
    const defaultMedia = TAB_DEFAULT_MEDIA[activeKey] || ''
    setProfitSearchTerm('')
    setProfitMediaFilter(defaultMedia)
    setProfitError('')
    setShowProfitModal(true)
  }, [activeKey])

  const handleProfitModalClose = useCallback(() => {
    setShowProfitModal(false)
  }, [])

  const loadProfitValidation = useCallback(async (monthParam) => {
    if (!monthParam) return
    setProfitValidationLoading(true)
    setProfitValidationError('')
    try {
      const response = await apiServices.data({
        path: 'billing/profit-validation',
        method: 'get',
        params: { month: monthParam },
      })
      if (response?.error) {
        throw response.error
      }
      setProfitValidationData({
        month: response?.month || monthParam,
        summary: response?.summary || null,
        differences: response?.differences || [],
      })
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        '取得利潤比對資料時發生錯誤，請稍後再試。'
      setProfitValidationError(message)
    } finally {
      setProfitValidationLoading(false)
    }
  }, [])

  const handleOpenProfitValidationModal = useCallback(() => {
    setProfitValidationSearchTerm('')
    setProfitValidationStatusFilter('')
    setProfitValidationError('')
    setShowProfitValidationModal(true)
  }, [])

  const handleProfitValidationModalClose = useCallback(() => {
    setShowProfitValidationModal(false)
  }, [])

  useEffect(() => {
    if (!showProfitModal) return
    loadProfitTable(currentMonthParam)
  }, [currentMonthParam, loadProfitTable, showProfitModal])

  useEffect(() => {
    if (!showProfitValidationModal) return
    loadProfitValidation(currentMonthParam)
  }, [currentMonthParam, loadProfitValidation, showProfitValidationModal])

  const loadRevenueSheet = useCallback(
    async (monthParam, tabKey, viewMode = REVENUE_VIEW_MODES.system) => {
      setRevenueLoadingByMode((prev) => ({
        ...prev,
        [viewMode]: true,
      }))
      setRevenueErrorByMode((prev) => ({
        ...prev,
        [viewMode]: '',
      }))
      try {
        const data = await fetchRevenueSheetData(monthParam, tabKey, viewMode)
        setRevenueDataByMode((prev) => ({
          ...prev,
          [viewMode]: data,
        }))
      } catch (_error) {
        setRevenueErrorByMode((prev) => ({
          ...prev,
          [viewMode]: '載入收益表失敗，請稍後再試。',
        }))
      } finally {
        setRevenueLoadingByMode((prev) => ({
          ...prev,
          [viewMode]: false,
        }))
      }
    },
    [fetchRevenueSheetData]
  )

  const handleOpenRevenueModal = useCallback(() => {
    const monthParam = (selectedMonth || DEFAULT_MONTH).replace('-', '')
    const tabKey = activeKey
    setRevenueTabKey(tabKey)
    setRevenueMonth(monthParam)
    setRevenueViewMode(REVENUE_VIEW_MODES.system)
    setRevenueDataByMode(createRevenueState(null))
    setRevenueLoadingByMode(createRevenueState(false))
    setRevenueErrorByMode(createRevenueState(''))
    setShowRevenueModal(true)
    loadRevenueSheet(monthParam, tabKey, REVENUE_VIEW_MODES.system)
  }, [activeKey, loadRevenueSheet, selectedMonth])

  const handleRevenueModalClose = useCallback(() => {
    setShowRevenueModal(false)
    setRevenueDataByMode(createRevenueState(null))
    setRevenueLoadingByMode(createRevenueState(false))
    setRevenueErrorByMode(createRevenueState(''))
    setRevenueTabKey('')
    setRevenueMonth('')
    setRevenueViewMode(REVENUE_VIEW_MODES.system)
  }, [])

  const handleExportRevenue = useCallback(async () => {
    const tabKey = revenueTabKey || activeKey
    if (!tabKey) return
    const monthParam =
      revenueMonth || (selectedMonth || DEFAULT_MONTH).replace('-', '')

    setExportError('')
    setExportingRevenue(true)
    try {
      let sheetData = currentRevenueData
      if (!sheetData) {
        sheetData = await fetchRevenueSheetData(
          monthParam,
          tabKey,
          revenueViewMode
        )
        setRevenueDataByMode((prev) => ({
          ...prev,
          [revenueViewMode]: sheetData,
        }))
      }

      if (!sheetData?.columns?.length || !sheetData?.rows?.length) {
        throw new Error('缺少收益表資料')
      }

      const workbook = new ExcelJS.Workbook()
      const sheetLabel =
        (revenueTabMeta?.label || tabKey || 'Revenue')
          .replace(/[\\/*?:]/g, '_')
          .replaceAll('[', '_')
          .replaceAll(']', '_')
          .slice(0, 31)
          .trim() || 'Revenue'
      const worksheet = workbook.addWorksheet(sheetLabel)

      worksheet.addRow(
        sheetData.columns.map((col) => col.displayName || col.key || '')
      )
      worksheet.addRow(sheetData.columns.map((col) => col.subLabel || ''))
      sheetData.rows.forEach((row) => {
        worksheet.addRow(
          sheetData.columns.map((col) =>
            row?.[col.key] !== undefined && row?.[col.key] !== null
              ? row[col.key]
              : ''
          )
        )
      })

      const buffer = await workbook.xlsx.writeBuffer()
      const blobData = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      const label = (revenueTabMeta?.label || tabKey).replace(/\s+/g, '_')
      link.href = url
      const viewLabel =
        revenueViewMode === REVENUE_VIEW_MODES.user
          ? '依使用者設定'
          : '系統比對'
      link.setAttribute(
        'download',
        `${monthParam}_${label}_${viewLabel}_收益表.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (_exportErr) {
      setExportError('匯出收益表失敗，請稍後再試。')
    } finally {
      setExportingRevenue(false)
    }
  }, [
    activeKey,
    currentRevenueData,
    fetchRevenueSheetData,
    revenueMonth,
    revenueTabKey,
    revenueTabMeta,
    revenueViewMode,
    selectedMonth,
  ])

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      const [insightsRes, budgetRes] = await Promise.all([
        apiServices.data({
          path: 'billing/campaign-insights',
          method: 'get',
          params: {
            startDate: inputRange.startDate,
            endDate: inputRange.endDate,
          },
        }),
        apiServices.data({
          path: 'billing/budget-list',
          method: 'get',
          params: {
            startDate: inputRange.startDate,
            endDate: inputRange.endDate,
          },
        }),
      ])

      if (insightsRes.error) throw insightsRes.error
      if (budgetRes.error) throw budgetRes.error

      setTabsData(insightsRes.tabs || [])
      setAppliedRange({
        startDate: insightsRes.startDate,
        endDate: insightsRes.endDate,
      })
      setBudgetData({
        rows: budgetRes.rows || [],
        mediaTotals: budgetRes.mediaTotals || [],
        totalAmount: budgetRes.totalAmount || 0,
        startDate: budgetRes.startDate,
        endDate: budgetRes.endDate,
      })
    } catch (_error) {
      setError('無法取得媒體成效資料，請稍後再試。')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadSavedAllocations(currentMonthParam)
  }, [currentMonthParam, loadSavedAllocations])

  useEffect(() => {
    if (
      !showRevenueModal ||
      !revenueMonth ||
      !revenueTabKey ||
      currentRevenueData ||
      currentRevenueLoading
    ) {
      return
    }
    loadRevenueSheet(revenueMonth, revenueTabKey, revenueViewMode)
  }, [
    currentRevenueData,
    currentRevenueLoading,
    loadRevenueSheet,
    revenueMonth,
    revenueTabKey,
    revenueViewMode,
    showRevenueModal,
  ])

  const renderAllocationStatus = (campaign) => {
    const allocations = allocationMap[campaign.campaignId] || []
    if (!allocations.length) {
      return <span className="text-muted">未分配</span>
    }
    return allocations.map((alloc, index) => (
      <div key={`${campaign.campaignId}-${alloc.projectCode || index}`}>
        <span className="fw-semibold">{alloc.projectCode}</span>
        {alloc.projectName ? ` ${alloc.projectName}` : ''}：
        {`${formatNumber(alloc.amount)} ${campaign.currency || ''}`}
        {alloc.ac ? `（AC ${alloc.ac}）` : ''}
      </div>
    ))
  }

  const renderTableBody = (campaigns, tabKey) => {
    if (!campaigns.length) {
      return (
        <tr>
          <td colSpan={5} className="text-center py-4 text-muted">
            無資料
          </td>
        </tr>
      )
    }

    return campaigns.map((campaign) => (
      <tr
        key={campaign.campaignId}
        onClick={() => handleCampaignClick(campaign, tabKey)}
        style={{ cursor: 'pointer' }}
      >
        <td>
          <div className="fw-semibold">{campaign.campaignName || '-'}</div>
          <div className="text-muted small">{campaign.campaignId || '-'}</div>
        </td>
        <td>
          <div>{campaign.accountName || '-'}</div>
          <div className="text-muted small">{campaign.accountId || '-'}</div>
        </td>
        <td className="text-center">{campaign.currency || '-'}</td>
        <td className="text-end">{formatNumber(campaign.spend)}</td>
        <td>{renderAllocationStatus(campaign)}</td>
      </tr>
    ))
  }

  const getAllocationSummary = useCallback(
    (campaigns = []) => {
      let allocated = 0
      let spend = 0
      campaigns.forEach((campaign) => {
        const campaignSpendValue = Number(campaign.spend) || 0
        spend += campaignSpendValue
        const allocations = allocationMap[campaign.campaignId] || []
        const allocationSum = allocations.reduce(
          (sum, alloc) => sum + (Number(alloc.amount) || 0),
          0
        )
        allocated += allocationSum
      })
      return {
        allocatedTotal: allocated,
        unallocatedTotal: Math.max(spend - allocated, 0),
      }
    },
    [allocationMap]
  )

  const renderBudgetTable = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )
    }

    if (!filteredBudgetRows.length) {
      return (
        <div className="text-center py-4 text-muted">無符合條件的預算資料</div>
      )
    }

    return (
      <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
        <Table responsive hover className="mb-0">
          <thead
            style={{
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1,
            }}
          >
            <tr>
              <th style={{ width: '140px' }}>專案代號</th>
              <th>專案名稱</th>
              <th style={{ width: '140px' }}>媒體</th>
              <th style={{ width: '160px' }} className="text-end">
                預算金額
              </th>
              <th style={{ width: '100px' }} className="text-center">
                幣別
              </th>
              <th style={{ width: '120px' }} className="text-center">
                AC
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredBudgetRows.map((row) => (
              <tr key={`${row.projectCode}-${row.mediaType}`}>
                <td className="fw-semibold">{row.projectCode}</td>
                <td>{row.projectName || '-'}</td>
                <td>{row.mediaType || row.expenseId}</td>
                <td className="text-end">{formatNumber(row.amount)}</td>
                <td className="text-center">{row.currency || 'TWD'}</td>
                <td className="text-center">
                  {row.ac || row.ac === 0
                    ? `${Number(row.ac).toFixed(1)}%`
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderRevenueTable = () => {
    if (currentRevenueLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )
    }

    if (currentRevenueError) {
      return <Alert variant="warning">{currentRevenueError}</Alert>
    }

    if (!currentRevenueData || !currentRevenueData.columns?.length) {
      return <div className="text-muted">目前沒有收益表資料</div>
    }

    if (!currentRevenueData.rows?.length) {
      return <div className="text-muted">沒有符合條件的收益表資料</div>
    }

    return (
      <div className="h-100">
        <Table hover className="mb-0 py-0">
          <thead style={REVENUE_HEADER_CELL_STYLE}>
            <tr className="text-nowrap">
              {currentRevenueData.columns.map((col) => (
                <th key={`${col.key}-header`}>
                  <div className="fw-semibold">
                    {col.displayName || col.key}
                  </div>
                  {col.subLabel ? (
                    <div className="text-muted small fw-normal">
                      {col.subLabel}
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRevenueData.rows.map((row, rowIndex) => (
              <tr key={`revenue-row-${rowIndex}`}>
                {currentRevenueData.columns.map((col) => (
                  <td key={`${rowIndex}-${col.key}`}>
                    {row?.[col.key] !== undefined ? row[col.key] : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderProfitTable = () => {
    if (profitLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )
    }

    if (profitError) {
      return <Alert variant="warning">{profitError}</Alert>
    }

    if (!filteredProfitRows.length) {
      return <div className="text-muted">沒有符合條件的利潤資料</div>
    }

    return (
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th style={{ minWidth: '220px' }}>專案</th>
              <th style={{ minWidth: '160px' }}>客戶</th>
              <th style={{ minWidth: '140px' }}>媒體</th>
              <th style={{ width: '160px' }} className="text-end">
                金額
              </th>
              <th style={{ width: '120px' }} className="text-center">
                指派狀態
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredProfitRows.map((row) => (
              <tr key={`${row.projectCode}-${row.media}`}>
                <td>
                  <div className="fw-semibold">
                    {row.projectName || '未命名專案'}
                  </div>
                  <div className="text-muted small">
                    {row.projectCode || '-'}
                  </div>
                </td>
                <td>{row.clientName || '-'}</td>
                <td>{row.media || '未分類'}</td>
                <td className="text-end">{formatNumber(row.amount)}</td>
                <td className="text-center">
                  {row.isTargeted ? (
                    <Badge bg="success" pill>
                      已指派
                    </Badge>
                  ) : (
                    <Badge bg="secondary" text="dark" pill>
                      未指派
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderProfitValidationTable = () => {
    if (!filteredProfitValidationRows.length) {
      return <div className="text-muted">沒有符合條件的比對資料</div>
    }

    return (
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr className="text-nowrap">
              <th style={{ minWidth: '220px' }}>專案</th>
              <th style={{ minWidth: '160px' }}>客戶</th>
              <th style={{ width: '180px' }} className="text-end">
                手動金額
              </th>
              <th style={{ width: '180px' }} className="text-end">
                貼表金額
              </th>
              <th style={{ width: '160px' }} className="text-end">
                差異
              </th>
              <th style={{ width: '140px' }}>狀態</th>
              <th style={{ minWidth: '200px' }}>備註</th>
            </tr>
          </thead>
          <tbody>
            {filteredProfitValidationRows.map((row) => {
              const key =
                row.projectCode || row.projectName || row.generatedRemark || ''
              const statusMeta = PROFIT_VALIDATION_STATUS_META[row.status] || {}
              const differenceClass =
                row.status === 'match' ? 'text-success' : 'text-danger'
              return (
                <tr key={`${key}-${row.status}`}>
                  <td>
                    <div className="fw-semibold">
                      {row.projectName || '未命名專案'}
                    </div>
                    <div className="text-muted small">
                      {row.projectCode || '-'}
                    </div>
                  </td>
                  <td>{row.clientName || '-'}</td>
                  <td className="text-end">
                    <div className="fw-semibold">
                      {formatNumber(row.manualAmount)}
                    </div>
                    <div className="text-muted small">
                      {row.manualMedia || '未提供'}
                      {row.isManualTargeted ? '／已指派' : ''}
                    </div>
                  </td>
                  <td className="text-end">
                    <div className="fw-semibold">
                      {formatNumber(row.generatedAmount)}
                    </div>
                    <div className="text-muted small">
                      {row.generatedMedia || '未提供'}
                    </div>
                  </td>
                  <td className={`text-end fw-semibold ${differenceClass}`}>
                    {formatSignedNumber(row.difference)}
                  </td>
                  <td>
                    <Badge
                      pill
                      bg={statusMeta.variant || 'secondary'}
                      text={statusMeta.text || undefined}
                    >
                      {statusMeta.label || row.status}
                    </Badge>
                  </td>
                  <td>
                    <div>{row.generatedRemark || '-'}</div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderProfitValidationContent = () => {
    if (profitValidationLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )
    }

    if (profitValidationError) {
      return <Alert variant="warning">{profitValidationError}</Alert>
    }

    if (!profitValidationSummary) {
      return <div className="text-muted">目前沒有比對結果</div>
    }

    return (
      <>
        <Row className="g-3 mb-3">
          <Col md={4} sm={6}>
            <SummaryCard
              title="手動利潤總額"
              value={formatNumber(profitValidationSummary.manualTotalAmount)}
              subTitle={`共 ${
                profitValidationSummary.manualProjects || 0
              } 個專案`}
            />
          </Col>
          <Col md={4} sm={6}>
            <SummaryCard
              title="貼表利潤總額"
              value={formatNumber(profitValidationSummary.generatedTotalAmount)}
              subTitle={`共 ${
                profitValidationSummary.generatedProjects || 0
              } 個專案`}
            />
          </Col>
          <Col md={4} sm={6}>
            <SummaryCard
              title="總差異"
              value={formatSignedNumber(
                profitValidationSummary.totalDifference
              )}
              subTitle={`容許誤差 ±${formatNumber(
                profitValidationSummary.tolerance || 0
              )} TWD`}
            />
          </Col>
        </Row>
        <div className="d-flex flex-wrap gap-2 mb-4">
          {PROFIT_VALIDATION_STATUS_BADGE_OPTIONS.map((option) => {
            const meta = PROFIT_VALIDATION_STATUS_META[option.value] || {}
            return (
              <Badge
                key={option.value}
                bg={meta.variant || 'secondary'}
                text={meta.text || undefined}
                className="px-3 py-2 text-nowrap"
              >
                {option.label}：{profitValidationCounts[option.value] || 0}
              </Badge>
            )
          })}
        </div>
        <Row className="g-3 mb-3 align-items-end">
          <Col md={5} sm={12}>
            <Form.Group controlId="profitValidationSearch">
              <Form.Label>搜尋（專案 / 客戶 / 媒體 / 備註）</Form.Label>
              <Form.Control
                type="text"
                placeholder="輸入關鍵字"
                value={profitValidationSearchTerm}
                onChange={(e) => setProfitValidationSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group controlId="profitValidationStatus">
              <Form.Label>狀態篩選</Form.Label>
              <Form.Select
                value={profitValidationStatusFilter}
                onChange={(e) =>
                  setProfitValidationStatusFilter(e.target.value)
                }
              >
                {PROFIT_VALIDATION_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="auto" className="d-flex align-items-end">
            <Button
              variant="outline-secondary"
              disabled={profitValidationLoading}
              onClick={() => loadProfitValidation(currentMonthParam)}
            >
              {profitValidationLoading ? '更新中…' : '重新整理'}
            </Button>
          </Col>
          <Col md="auto" className="d-flex align-items-end text-muted small">
            共 {profitValidationSummary.totalProjects || 0} 個專案
          </Col>
        </Row>
        {renderProfitValidationTable()}
      </>
    )
  }

  return (
    <div className="container-fluid">
      <Row className="align-items-end mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Form.Group controlId="monthPicker">
            <Form.Label>月份</Form.Label>
            <Form.Control
              type="month"
              value={selectedMonth}
              onChange={(e) => {
                const { value } = e.target
                setSelectedMonth(value)
                setInputRange(getMonthRange(value))
              }}
            />
          </Form.Group>
        </Col>
        <Col md="auto" className="mb-3">
          <Button onClick={fetchData} disabled={loading}>
            {loading ? '載入中…' : '查詢'}
          </Button>
        </Col>
        <Col md="auto" className="mb-3">
          <Button variant="primary" onClick={() => setShowBudgetModal(true)}>
            查看預算清單
          </Button>
        </Col>
        <Col md="auto" className="mb-3">
          <Button
            variant="success"
            disabled={loading}
            onClick={handleOpenRevenueModal}
          >
            查看收益表（{activeTabMeta?.label || '當前頁籤'}）
          </Button>
        </Col>
        <Col md="auto" className="mb-3">
          <Button
            variant="outline-primary"
            disabled={loading}
            onClick={handleOpenProfitModal}
          >
            查看利潤表資料
          </Button>
        </Col>
        <Col md="auto" className="mb-3">
          <Button
            variant="outline-warning"
            disabled={loading}
            onClick={handleOpenProfitValidationModal}
          >
            利潤貼表比對
          </Button>
        </Col>
        <Col className="text-end text-muted small">
          目前範圍：{appliedRange.startDate} ~ {appliedRange.endDate}
        </Col>
      </Row>

      <Row className="align-items-end mb-4">
        <Col md={4} sm={6} className="mb-3">
          <Form.Group controlId="searchTerm">
            <Form.Label>搜尋（專案 / 帳戶 / 活動）</Form.Label>
            <Form.Control
              type="text"
              placeholder="輸入關鍵字"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {exportError && (
        <Alert
          variant="warning"
          className="mb-3"
          onClose={() => setExportError('')}
          dismissible
        >
          {exportError}
        </Alert>
      )}

      <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
        <Nav variant="tabs" className="mb-3 flex-wrap">
          {TAB_META.map((tab) => {
            const tabData = tabsMap[tab.key]
            return (
              <Nav.Item key={tab.key} className="me-2 mb-2">
                <Nav.Link
                  eventKey={tab.key}
                  className="d-flex align-items-center"
                >
                  <span>{tab.label}</span>
                  <Badge bg="secondary" pill className="ms-2">
                    {tabData ? tabData.campaignCount : 0}
                  </Badge>
                </Nav.Link>
              </Nav.Item>
            )
          })}
        </Nav>

        <Tab.Content>
          {TAB_META.map((tab) => {
            const tabData = tabsMap[tab.key] || {
              key: tab.key,
              label: tab.label,
              currency: tab.currency,
              totalSpend: 0,
              campaignCount: 0,
              campaigns: [],
            }
            const filteredCampaigns = filterCampaigns(tabData.campaigns)
            const { allocatedTotal, unallocatedTotal } = getAllocationSummary(
              tabData.campaigns
            )
            return (
              <Tab.Pane key={tab.key} eventKey={tab.key}>
                <Row className="mb-3">
                  <Col md={3} sm={6}>
                    <SummaryCard
                      title="總花費"
                      value={`${formatNumber(tabData.totalSpend)} ${
                        tabData.currency || ''
                      }`}
                      subTitle={`共 ${filteredCampaigns.length} 個 Campaign`}
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <SummaryCard
                      title="已分配金額"
                      value={`${formatNumber(allocatedTotal)} ${
                        tabData.currency || ''
                      }`}
                      subTitle="已分配給專案"
                    />
                  </Col>
                  <Col md={3} sm={6}>
                    <SummaryCard
                      title="未分配金額"
                      value={`${formatNumber(unallocatedTotal)} ${
                        tabData.currency || ''
                      }`}
                      subTitle="待分配金額"
                    />
                  </Col>
                </Row>

                <Card className="shadow-sm">
                  <Card.Body className="p-0">
                    {loading ? (
                      <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" />
                      </div>
                    ) : (
                      <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                        <Table responsive hover className="mb-0">
                          <thead
                            style={{
                              position: 'sticky',
                              top: 0,
                              background: '#fff',
                              zIndex: 1,
                            }}
                          >
                            <tr>
                              <th style={{ minWidth: '220px' }}>Campaign</th>
                              <th style={{ minWidth: '220px' }}>帳戶</th>
                              <th
                                style={{ width: '80px' }}
                                className="text-center"
                              >
                                幣別
                              </th>
                              <th
                                style={{ width: '160px' }}
                                className="text-end"
                              >
                                花費
                              </th>
                              <th
                                style={{ minWidth: '180px' }}
                                className="text-start"
                              >
                                分配狀況
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {renderTableBody(filteredCampaigns, tab.key)}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            )
          })}
        </Tab.Content>
      </Tab.Container>

      <Modal
        // size="xl"
        show={showRevenueModal}
        onHide={handleRevenueModalClose}
        centered
        fullscreen
      >
        <Modal.Header closeButton className="align-items-center">
          <div className="d-flex w-100 h-100 align-items-center justify-content-between">
            <Modal.Title className="mb-0">
              收益表 - {revenueTabMeta?.label || '當前頁籤'}（
              {revenueMonthLabel}）
            </Modal.Title>
            <Button
              variant="outline-success"
              size="sm"
              disabled={exportingRevenue}
              onClick={handleExportRevenue}
            >
              {exportingRevenue ? '匯出中…' : '匯出'}
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center align-items-start gap-2 py-3">
            <div>
              <div className="fw-semibold">檢視模式</div>
              <div className="text-muted small">
                {revenueViewMode === REVENUE_VIEW_MODES.user
                  ? '僅顯示使用者已儲存的手動分配，忽略系統比對。'
                  : '依照系統自動比對結果顯示收益表。'}
              </div>
            </div>
            <ButtonGroup>
              {REVENUE_VIEW_TABS.map((tab) => (
                <Button
                  key={tab.key}
                  size="sm"
                  variant={
                    revenueViewMode === tab.key ? 'primary' : 'outline-primary'
                  }
                  onClick={() => handleRevenueViewModeChange(tab.key)}
                >
                  {tab.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          {renderRevenueTable()}
        </Modal.Body>
      </Modal>

      <Modal
        size="xl"
        show={showProfitModal}
        onHide={handleProfitModalClose}
        centered
      >
        <Modal.Header closeButton className="align-items-center">
          <div className="d-flex flex-column flex-md-row w-100 justify-content-between">
            <Modal.Title className="mb-0">
              利潤表資料（{profitMonthLabel}）
            </Modal.Title>
            <div className="text-muted small mt-2 mt-md-0">
              顯示 {filteredProfitRows.length} / {profitData.rows.length} 筆
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3 mb-3">
            <Col md={5} sm={12}>
              <Form.Group controlId="profitSearchTerm">
                <Form.Label>搜尋（專案 / 客戶 / 媒體）</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="輸入關鍵字"
                  value={profitSearchTerm}
                  onChange={(e) => setProfitSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4} sm={6}>
              <Form.Group controlId="profitMediaFilter">
                <Form.Label>媒體篩選</Form.Label>
                <Form.Select
                  value={profitMediaFilter}
                  onChange={(e) => setProfitMediaFilter(e.target.value)}
                >
                  <option value="">全部媒體</option>
                  {profitMediaOptions.map((media) => (
                    <option key={media} value={media}>
                      {media}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md="auto" className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                disabled={profitLoading}
                onClick={() => loadProfitTable(currentMonthParam)}
              >
                {profitLoading ? '更新中…' : '重新整理'}
              </Button>
            </Col>
          </Row>
          <Row className="g-3 mb-4">
            <Col md={4} sm={6}>
              <SummaryCard
                title="全部專案總額"
                value={formatNumber(profitData.totalAmount)}
                subTitle="API 回傳全部資料"
              />
            </Col>
            <Col md={4} sm={6}>
              <SummaryCard
                title="篩選後總額"
                value={formatNumber(filteredProfitTotal)}
                subTitle={`共 ${filteredProfitRows.length} 筆`}
              />
            </Col>
            <Col md={4} sm={6}>
              <SummaryCard
                title="已指派筆數"
                value={`${filteredProfitTargetedCount} 筆`}
                subTitle="目前篩選條件"
              />
            </Col>
          </Row>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {filteredProfitMediaTotals.length ? (
              filteredProfitMediaTotals.map((media) => (
                <Card key={media.media || '未分類'} className="shadow-sm">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small">
                      {media.media || '未分類'}
                    </div>
                    <div className="fw-bold">{formatNumber(media.amount)}</div>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="text-muted">目前篩選沒有媒體分類資料</div>
            )}
          </div>
          {renderProfitTable()}
        </Modal.Body>
      </Modal>

      <Modal
        size="xl"
        show={showProfitValidationModal}
        onHide={handleProfitValidationModalClose}
        centered
      >
        <Modal.Header closeButton className="align-items-center">
          <div className="d-flex flex-column flex-md-row w-100 justify-content-between">
            <Modal.Title className="mb-0">
              利潤貼表比對（{profitValidationMonthLabel}）
            </Modal.Title>
            <div className="text-muted small mt-2 mt-md-0">
              顯示 {filteredProfitValidationRows.length} /{' '}
              {profitValidationRows.length} 筆
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>{renderProfitValidationContent()}</Modal.Body>
      </Modal>

      <Modal
        size="lg"
        show={showAllocationModal}
        onHide={handleAllocationModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>花費分配</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCampaign && (
            <>
              <div className="mb-4">
                <div className="fw-semibold fs-5">
                  {selectedCampaign.campaignName || '未命名 Campaign'}
                </div>
                <div className="text-muted small">
                  Campaign ID：{selectedCampaign.campaignId || '-'}
                </div>
                <div className="text-muted small">
                  帳戶：{selectedCampaign.accountName || '-'}（
                  {selectedCampaign.accountId || '-'}）
                </div>
                <div className="mt-2">
                  <div className="text-muted small">總金額</div>
                  <div className="fs-4 fw-bold">
                    {`${formatNumber(selectedCampaign.spend)} ${
                      selectedCampaign.currency || ''
                    }`}
                  </div>
                </div>
              </div>

              <div className="border rounded p-3 bg-light mb-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div className="text-muted small">最後儲存時間</div>
                    <div className="fw-semibold">
                      {selectedCampaignMeta
                        ? formatTimestamp(selectedCampaignMeta.createdAt)
                        : '尚未儲存'}
                    </div>
                    {selectedCampaignMeta?.updatedBy?.name && (
                      <div className="text-muted small">
                        由 {selectedCampaignMeta.updatedBy.name}
                        {selectedCampaignMeta.updatedBy.email
                          ? `（${selectedCampaignMeta.updatedBy.email}）`
                          : ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleViewAllocationHistory}
                      disabled={isHistoryLoading}
                    >
                      {isHistoryLoading ? '載入歷史中…' : '查看歷史紀錄'}
                    </Button>
                  </div>
                </div>
                {isHistoryLoading && (
                  <div className="text-muted small mt-2">載入歷史資料中…</div>
                )}
                {!isHistoryLoading &&
                  hasHistoryLoaded &&
                  selectedCampaignHistory.length > 0 && (
                    <div
                      className="mt-3 bg-white rounded border"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {selectedCampaignHistory.map((record) => {
                        const historyTotal = (record.allocations || []).reduce(
                          (sum, item) => sum + (Number(item.amount) || 0),
                          0
                        )
                        const historyKey =
                          record.id ||
                          record.createdAt ||
                          `${record.campaignId}-${historyTotal}`
                        return (
                          <div
                            key={historyKey}
                            className="px-3 py-2 border-bottom small"
                          >
                            <div className="d-flex justify-content-between gap-3">
                              <div>
                                <div className="fw-semibold">
                                  {formatTimestamp(record.createdAt)}
                                </div>
                                {record.updatedBy?.name && (
                                  <div className="text-muted">
                                    {record.updatedBy.name}
                                    {record.updatedBy.email
                                      ? `（${record.updatedBy.email}）`
                                      : ''}
                                  </div>
                                )}
                              </div>
                              <div className="text-end">
                                <div className="text-nowrap">
                                  筆數：{record.allocations?.length || 0}
                                </div>
                                <div className="text-nowrap">
                                  總額：{formatNumber(historyTotal)}{' '}
                                  {selectedCampaign?.currency || ''}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                {!isHistoryLoading &&
                  hasHistoryLoaded &&
                  selectedCampaignHistory.length === 0 && (
                    <div className="text-muted small mt-2">尚無歷史紀錄</div>
                  )}
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">金額分配</div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddAllocationRow}
                >
                  <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
                  新增分配
                </Button>
              </div>

              {allocationRows.map((row, index) => {
                const selectedOption = projectOptions.find(
                  (opt) => opt.projectCode === row.projectCode
                )
                return (
                  <Row key={row.id} className="g-3 align-items-end mb-3">
                    <Col md={5} sm={12}>
                      <Form.Group controlId={`allocationProject-${row.id}`}>
                        <Form.Label>專案（第 {index + 1} 筆）</Form.Label>
                        <Typeahead
                          id={`typeahead-${row.id}`}
                          labelKey="label"
                          clearButton
                          onChange={(selected) =>
                            handleAllocationProjectChange(
                              row.id,
                              selected[0]?.projectCode || ''
                            )
                          }
                          options={projectOptions}
                          selected={selectedOption ? [selectedOption] : []}
                          placeholder="搜尋專案代號 / 名稱"
                          renderMenuItemChildren={(option) => (
                            <div>
                              <div className="fw-semibold">
                                {option.projectCode}
                              </div>
                              <div className="text-muted small">
                                {option.projectName || '未命名專案'}
                              </div>
                            </div>
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId={`allocationAmount-${row.id}`}>
                        <Form.Label>分配金額</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={row.amount}
                          onChange={(e) =>
                            handleAllocationFieldChange(
                              row.id,
                              'amount',
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3} sm={6}>
                      <Form.Group controlId={`allocationAc-${row.id}`}>
                        <Form.Label>AC %</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="0"
                          value={row.ac}
                          onChange={(e) =>
                            handleAllocationFieldChange(
                              row.id,
                              'ac',
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1} sm={12} className="text-end">
                      <Button
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => handleRemoveAllocationRow(row.id)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </Button>
                    </Col>
                  </Row>
                )
              })}

              {!allocationRows.length && (
                <div className="text-muted mb-3">尚未新增分配</div>
              )}

              <div className="mt-3">
                <div className="d-flex justify-content-between">
                  <span>分配總額</span>
                  <span>
                    {formatNumber(allocationTotal)} /{' '}
                    {formatNumber(campaignSpend)} {selectedCampaign.currency}
                  </span>
                </div>
                {!allocationMatchesTotal && selectedCampaign && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    分配金額需要等於總金額才可儲存。
                  </Alert>
                )}
              </div>

              {allocationSaveError && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {allocationSaveError}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAllocationModalClose}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={!isAllocationSaveEnabled || savingAllocation}
            onClick={handleAllocationSave}
          >
            {savingAllocation ? '儲存中…' : '儲存'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        size="xl"
        show={showBudgetModal}
        onHide={() => setShowBudgetModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>預算清單明細</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted small">預算總額</div>
            <div className="fs-4 fw-bold">
              {`${formatNumber(budgetData.totalAmount)} TWD`}
            </div>
            <div className="text-muted small">
              共 {budgetData.rows.length} 筆，期間：
              {budgetData.startDate || appliedRange.startDate} ~{' '}
              {budgetData.endDate || appliedRange.endDate}
            </div>
          </div>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {budgetData.mediaTotals.length ? (
              budgetData.mediaTotals.map((media) => (
                <Card key={media.media} className="shadow-sm">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small">{media.media}</div>
                    <div className="fw-bold">
                      {formatNumber(media.amount)} TWD
                    </div>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="text-muted">無媒體分類資料</div>
            )}
          </div>
          {renderBudgetTable()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBudgetModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

SummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subTitle: PropTypes.string,
}

SummaryCard.defaultProps = {
  subTitle: '',
}
